/**
 * Cartão do ciclo em PNG (880×1160), desenhado no canvas como no
 * Meu Perfil.dc.html. `desenharCartao` só desenha, para ser testável com um
 * contexto 2D falso; `gerarCartaoPng` carrega as imagens e devolve o data URL.
 */
import { medirAvatar } from './avatar-no-cartao';

export interface DadosDoCartao {
  nome: string;
  /** "DIRETORIA · Ciclo 2S2026" */
  linhaDeApoio: string;
  pontos: string;
  nivel?: string;
  ciclo: string;
}

export const LARGURA_DO_CARTAO = 880;
export const ALTURA_DO_CARTAO = 1160;

/** Nomes longos diminuem até caber entre as margens do cartão. */
const LARGURA_UTIL_DO_NOME = 760;

const AZUL = '#465EFF';
const AMARELO = '#FCFC30';
const LILAS = '#BDB6FF';
const TITULOS = (peso: number, tamanho: number) => `${peso} ${tamanho}px "BancoDoBrasil Titulos", system-ui, sans-serif`;
const TEXTOS = (peso: number, tamanho: number) => `${peso} ${tamanho}px "BancoDoBrasil Textos", system-ui, sans-serif`;

export function nomeDoArquivoDoCartao(ciclo: string) {
  return `joga-junto-bb-cartao-${ciclo}.png`;
}

type Imagem = CanvasImageSource & { width: number; height: number };

/** Avatar montado já carregado: fundo do círculo e camadas na posição medida (PERFIL-51). */
export interface AvatarDesenhavel {
  fundo?: string;
  camadas: { imagem: Imagem; x: number; y: number; largura: number; altura: number }[];
}

/** Diâmetro do círculo da foto no cartão. */
export const DIAMETRO_DA_FOTO = 300;

function caixa(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  c.beginPath();
  if (c.roundRect) c.roundRect(x, y, w, h, r);
  else c.rect(x, y, w, h);
  c.fill();
}

export function desenharCartao(
  c: CanvasRenderingContext2D,
  dados: DadosDoCartao,
  logo: Imagem | null,
  foto: Imagem | null,
  avatar?: AvatarDesenhavel | null,
) {
  const W = LARGURA_DO_CARTAO;
  const cx = W / 2;

  c.fillStyle = AZUL;
  caixa(c, 0, 0, W, ALTURA_DO_CARTAO, 56);

  if (logo) {
    const larguraDoLogo = 230;
    c.drawImage(logo, 62, 66, larguraDoLogo, logo.height * (larguraDoLogo / logo.width));
  }

  c.textAlign = 'center';
  c.font = TITULOS(700, 30);
  const larguraDosPontos = c.measureText(dados.pontos).width + 56;
  c.fillStyle = 'rgba(255,255,255,0.18)';
  caixa(c, W - 62 - larguraDosPontos, 92, larguraDosPontos, 62, 31);
  c.fillStyle = '#FFFFFF';
  c.fillText(dados.pontos, W - 62 - larguraDosPontos / 2, 133);

  // Círculo lilás sempre: é o fundo da foto e o que fica quando ela não carrega.
  const cy = 470;
  const raio = DIAMETRO_DA_FOTO / 2;
  c.save();
  c.beginPath();
  c.arc(cx, cy, raio, 0, Math.PI * 2);
  c.closePath();
  c.fillStyle = LILAS;
  c.fill();
  c.clip();
  if (foto) {
    const escala = Math.max((raio * 2) / foto.width, (raio * 2) / foto.height);
    c.drawImage(foto, cx - (foto.width * escala) / 2, cy - (foto.height * escala) / 2, foto.width * escala, foto.height * escala);
  } else if (avatar?.camadas.length) {
    if (avatar.fundo) {
      c.fillStyle = avatar.fundo;
      c.fill();
    }
    avatar.camadas.forEach(camada => {
      c.drawImage(camada.imagem, cx - raio + camada.x, cy - raio + camada.y, camada.largura, camada.altura);
    });
  }
  c.restore();
  c.lineWidth = 10;
  c.strokeStyle = AMARELO;
  c.beginPath();
  c.arc(cx, cy, raio + 5, 0, Math.PI * 2);
  c.stroke();

  c.fillStyle = '#FFFFFF';
  let tamanhoDoNome = 60;
  c.font = TITULOS(800, tamanhoDoNome);
  while (tamanhoDoNome > 32 && c.measureText(dados.nome).width > LARGURA_UTIL_DO_NOME) {
    tamanhoDoNome -= 2;
    c.font = TITULOS(800, tamanhoDoNome);
  }
  c.fillText(dados.nome, cx, 706);
  c.fillStyle = 'rgba(255,255,255,0.88)';
  c.font = TEXTOS(400, 27);
  c.fillText(dados.linhaDeApoio, cx, 752);

  c.strokeStyle = 'rgba(255,255,255,0.35)';
  c.lineWidth = 2;
  c.beginPath();
  c.moveTo(130, 838);
  c.lineTo(W - 130, 838);
  c.stroke();

  if (dados.nivel) {
    const textoDoNivel = `Nível ${dados.nivel}`;
    c.font = TITULOS(700, 34);
    const larguraDoNivel = c.measureText(textoDoNivel).width + 76;
    c.fillStyle = AMARELO;
    caixa(c, cx - larguraDoNivel / 2, 894, larguraDoNivel, 78, 39);
    c.fillStyle = AZUL;
    c.fillText(textoDoNivel, cx, 945);
  }

  c.font = TEXTOS(400, 24);
  c.fillStyle = 'rgba(255,255,255,0.72)';
  c.fillText('Quem #JogaJunto merece aparecer.', cx, 1070);
}

function carregarImagem(src?: string): Promise<HTMLImageElement | null> {
  if (!src) return Promise.resolve(null);

  return new Promise(resolve => {
    const imagem = new Image();
    imagem.crossOrigin = 'anonymous';
    imagem.onload = () => resolve(imagem);
    imagem.onerror = () => resolve(null);
    imagem.src = src;
  });
}

function novoCanvas() {
  const canvas = document.createElement('canvas');
  canvas.width = LARGURA_DO_CARTAO;
  canvas.height = ALTURA_DO_CARTAO;
  const contexto = canvas.getContext('2d');
  if (!contexto) throw new Error('Canvas indisponível');

  return { canvas, contexto };
}

/** Carrega as camadas do avatar montado; sem nenhuma camada válida, não há avatar. */
async function carregarAvatar(config?: object): Promise<AvatarDesenhavel | null> {
  if (!config) return null;
  const medido = medirAvatar(config, DIAMETRO_DA_FOTO);
  const imagens = await Promise.all(medido.camadas.map(camada => carregarImagem(camada.src)));
  const camadas = medido.camadas.flatMap((camada, i) => {
    const imagem = imagens[i];
    return imagem ? [{ ...camada, imagem }] : [];
  });

  return camadas.length ? { fundo: medido.fundo, camadas } : null;
}

/**
 * @param avatarConfig configuração do avatar montado, usada quando não há foto (PERFIL-51).
 */
export async function gerarCartaoPng(dados: DadosDoCartao, logoSrc: string, fotoSrc?: string, avatarConfig?: object): Promise<string> {
  await document.fonts?.ready.catch(() => undefined);

  const [logo, foto, avatar] = await Promise.all([
    carregarImagem(logoSrc),
    carregarImagem(fotoSrc),
    fotoSrc ? Promise.resolve(null) : carregarAvatar(avatarConfig),
  ]);
  const comFoto = novoCanvas();
  desenharCartao(comFoto.contexto, dados, logo, foto, avatar);

  try {
    return comFoto.canvas.toDataURL('image/png');
  } catch (e) {
    // Foto sem CORS "suja" o canvas para sempre: a versão sem foto sai de um canvas novo (PERFIL-41).
    const semFoto = novoCanvas();
    desenharCartao(semFoto.contexto, dados, logo, null);
    return semFoto.canvas.toDataURL('image/png');
  }
}

export function baixarArquivo(dataUrl: string, nome: string) {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = nome;
  document.body.appendChild(link);
  link.click();
  link.remove();
}
